import React from 'react';
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Button, 
  Box, 
  Chip,
  useTheme 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface PrototypeCardProps {
  title: string;
  description: string;
  targetUser: string;
  features: string[];
  route: string;
  icon: React.ReactNode;
  imageUrl?: string;
  badges?: string[];
}

export const PrototypeCard: React.FC<PrototypeCardProps> = ({
  title,
  description,
  targetUser,
  features,
  route,
  icon,
  imageUrl,
  badges = []
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleNavigate = () => {
    navigate(route);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease-in-out',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
          borderColor: 'primary.main',
        },
        cursor: 'pointer',
      }}
      onClick={handleNavigate}
    >
      {/* Header with Icon */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ fontSize: '3rem' }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {targetUser}
          </Typography>
        </Box>
      </Box>

      {/* Preview Image */}
      {imageUrl && (
        <CardMedia
          component="img"
          height="200"
          image={imageUrl}
          alt={`${title} preview`}
          sx={{
            objectFit: 'cover',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        />
      )}

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
        {/* Badges */}
        {badges.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {badges.map((badge, index) => (
              <Chip
                key={index}
                label={badge}
                size="small"
                color="secondary"
                variant="outlined"
              />
            ))}
          </Box>
        )}

        {/* Description */}
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ mb: 3, lineHeight: 1.6 }}
        >
          {description}
        </Typography>

        {/* Features List */}
        <Box sx={{ mb: 3, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Key Features:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {features.map((feature, index) => (
              <Box 
                component="li" 
                key={index}
                sx={{ 
                  mb: 0.5,
                  typography: 'body2',
                  color: 'text.secondary',
                }}
              >
                {feature}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Call-to-Action Button */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          sx={{
            mt: 'auto',
            py: 1.5,
            fontWeight: 600,
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleNavigate();
          }}
        >
          Try {title}
        </Button>
      </CardContent>
    </Card>
  );
};